import type { Plugin } from "../../../core/plugin";
import { PluginRegistry } from "../../../core/pluginRegistry";

/**
 * ltnCounter — conteo animado para las cifras de "Datos de red" (LTNW-1).
 *
 * Sustituye el `statsRef` + estado `{{ n1 }}…{{ n4 }}` de React del
 * prototipo. Cada valor anima de 0 a su objetivo la primera vez que el
 * bloque entra en el viewport al hacer scroll, y se queda fijo.
 *
 * El marcado lleva el valor final como texto para que la cifra sea correcta
 * con JavaScript desactivado y para los crawlers — el plugin lo lee, lo
 * resetea a cero y lo anima hacia arriba. Se conservan prefijos/sufijos
 * ("+", "%").
 *
 * Contrato de marcado:
 *   <div data-plugin="ltnCounter">
 *     <span data-ltn-count>+20</span>
 *
 * Opcional: data-ltn-count-duration (ms, por defecto 1600) en el elemento
 * anfitrión.
 */
export class LtnCounter implements Plugin {
  private observer: IntersectionObserver | null = null;
  private frame = 0;
  private targets: {
    node: HTMLElement;
    value: number;
    prefix: string;
    suffix: string;
    decimals: number;
    /** Texto original del marcado, restaurado tal cual en el frame final
     *  para que el formato diseñado gane a lo que Intl pudiera re-derivar. */
    final: string;
  }[] = [];

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    const nodes =
      this.element.querySelectorAll<HTMLElement>("[data-ltn-count]");

    this.targets = [...nodes].flatMap((node) => {
      const raw = (node.textContent || "").trim();
      // Separa "+1.234,5 %" en prefijo / número / sufijo.
      const match = raw.match(/^(\D*?)([\d.,]+)(.*)$/);
      if (!match) return [];

      const [, prefix, digits, suffix] = match;
      // Formato español: "." agrupa los miles, "," es la marca decimal.
      const normalized = digits.replace(/\./g, "").replace(",", ".");
      const value = Number(normalized);
      if (!Number.isFinite(value)) return [];

      const decimals = normalized.includes(".")
        ? normalized.split(".")[1].length
        : 0;

      return [{ node, value, prefix, suffix, decimals, final: raw }];
    });

    if (!this.targets.length) return;

    // Respeta la preferencia de movimiento del usuario — muestra los
    // valores finales de inmediato.
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion || !("IntersectionObserver" in window)) return;

    this.render(0);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.disconnect();
        this.animate();
      },
      { threshold: 0.35 },
    );
    this.observer.observe(this.element);
  }

  destroy(): void {
    this.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  private disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private animate(): void {
    const duration = Number(this.element.dataset.ltnCountDuration) || 1600;
    const start = performance.now();

    const step = (now: number) => {
      const elapsed = now - start;
      const linear = Math.min(1, elapsed / duration);
      // easeOutCubic — arranque rápido, asentamiento suave.
      const eased = 1 - Math.pow(1 - linear, 3);

      this.render(eased);

      if (linear < 1) this.frame = requestAnimationFrame(step);
    };

    this.frame = requestAnimationFrame(step);
  }

  private render(progress: number): void {
    for (const { node, value, prefix, suffix, decimals, final } of this
      .targets) {
      if (progress >= 1) {
        node.textContent = final;
        continue;
      }

      const current = (value * progress).toFixed(decimals);
      const [whole, fraction] = current.split(".");
      const grouped = Number(whole).toLocaleString("es-ES");
      node.textContent =
        prefix + (fraction ? `${grouped},${fraction}` : grouped) + suffix;
    }
  }
}

PluginRegistry.register("ltnCounter", LtnCounter);
